// External Imports
import Rollbar from 'rollbar';

// Internal Imports
import { isNode } from './environment.mjs';
import { getRuntimeConfiguration } from './getRuntimeConfiguration.mjs';

// Local Variables
const accessToken = isNode()
  ? getRuntimeConfiguration('ROLLBAR_ACCESS_TOKEN_SERVER')
  : getRuntimeConfiguration('ROLLBAR_ACCESS_TOKEN_CLIENT');

const browserConfiguration = {
  hostSafeList: ['cloudfront.net', 'babbel.com', 'babbel.cn'],
};

// Local Functions
const checkIgnore = (isUncaught, parameters, payload) => {
  if (!isUncaught) {
    // We want to track all errors we report ourselves
    return false;
  }
  const { class: exceptionClass, stack } = payload.body.trace.exception;
  // Ignore any errors that are caused by third-parties by checking truthiness of error.stack.
  if (!stack) {
    return true;
  }
  if (exceptionClass === 'AbortError') {
    return true;
  }
  const hasAtLeastOneRelevantFrame = payload.body.trace.frames.some(({ filename, method }) => {
    const isUnknownFilename = filename === '(unknown)';
    const isNativeCodeFilename = filename === '[native code]';
    const location = typeof globalThis === 'undefined' ? undefined : globalThis.location;
    // Stack frames almost always contain the current URL in some form somewhere in their stack as the origin, so those frames aren't relevant
    const isCurrentURL =
      location !== undefined && filename === `${location.origin}${location.pathname}`;
    // It's likely that errors from anonymous methods do not belong to this codebase and thus are not relevant.
    const isAnonymous = method === '[anonymous]';
    if (isUnknownFilename || isNativeCodeFilename || isCurrentURL || isAnonymous) {
      return false;
    }
    let fileAsUrl;
    // Rollbar is often part of the stack frames as it's the one listening to the error
    // so if a stack has only rollbar frames, we consider it irrelevant
    const isRollbarFrame =
      filename.includes('node_modules/rollbar') || filename.includes('rollbar/src/browser');
    try {
      fileAsUrl = new URL(filename);
    } catch {
      // If the filename wasn't a URL, it was a relative path from the root of the project.
      // In this case it's relevant except if it's a Rollbar frame.
      return !isRollbarFrame;
    }
    const isDomainFrame = browserConfiguration.hostSafeList.some((host) =>
      fileAsUrl.host.endsWith(host),
    );
    const isGlobalCode = method === 'global code';
    return isDomainFrame && !isRollbarFrame && !isGlobalCode;
  });
  return !hasAtLeastOneRelevantFrame;
};

const rollbarConfig = {
  accessToken,
  captureUncaught: true,
  captureUnhandledRejections: true,
  checkIgnore,
  payload: {
    client: {
      javascript: {
        code_version: BUILD_TIME_CONFIGURATION_COMMIT_SHA,
        guess_uncaught_frames: true,
        source_map_enabled: true,
      },
    },
    environment: getRuntimeConfiguration('ENVIRONMENT'),
    server: {
      root: 'webpack:///./',
    },
  },
  ...(isNode() ? {} : browserConfiguration),
};

const logger = accessToken
  ? new Rollbar(rollbarConfig)
  : {
      configure: () => {},
      // eslint-disable-next-line no-console -- we need the consoles here as we are mimicking rollbar for local dev
      critical: console.error.bind(console),
      // eslint-disable-next-line no-console -- we need the consoles here as we are mimicking rollbar for local dev
      debug: console.log.bind(console),
      // eslint-disable-next-line no-console -- we need the consoles here as we are mimicking rollbar for local dev
      error: console.error.bind(console),
      // eslint-disable-next-line no-console -- we need the consoles here as we are mimicking rollbar for local dev
      info: console.log.bind(console),
      lambdaHandler: (lambdaFunction) => lambdaFunction,
      // eslint-disable-next-line no-console -- we need the consoles here as we are mimicking rollbar for local dev
      warning: console.warn.bind(console),
    };

const reportWordPressFetchingError = (message, error) => {
  if (error.name === 'TimeoutError' || error instanceof TypeError) {
    logger.warning(message, error);
    return;
  }
  logger.error(message, error);
};

// Module Exports
export { checkIgnore, logger, reportWordPressFetchingError };
